import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, Save, Download, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const today = () => new Date().toISOString().slice(0, 10);

const D4PS_DEFAULTS = {
  transferee_full_name: "D4PS Couriers & Logistics Ltd",
  transferee_company_address: "D4PS Couriers & Logistics Ltd\nOffice F3 Globe House Business Centre\nGlobe Park, Kingsway West\nMoss Bridge Road\nRochdale",
  transferee_postcode: "OL16 5EB",
  transferee_carrier_no: "CBDU626462",
  transferee_details: "Upper Tier Waste Carrier, Broker and Dealer",
  transferee_signature_role: "D4PS Couriers & Logistics Ltd",
};

const CONTAINER_OPTIONS = ["Loose", "Sacks", "Skip", "Drum", "Other"];
const TRANSFEROR_TYPES = ["Producer", "Importer", "Local authority", "Permit holder", "Waste carrier"];

const empty = () => ({
  // Section A
  waste_description: "",
  ewc_codes: "",
  container_type: "",
  quantity_description: "",

  // Section B — Transferor
  transferor_full_name: "",
  transferor_company_address: "",
  transferor_postcode: "",
  transferor_sic_code: "",
  transferor_local_authority: "",
  transferor_type: "Producer",

  // Section C — Transferee (pre-fill with D4PS defaults)
  ...D4PS_DEFAULTS,
  transfer_address: "",
  transfer_postcode: "",
  facility: "",
  date: today(),
  transfer_time: "",

  // Broker
  broker_registration_no: "",

  // Section D
  transferor_signature_name: "",
  transferor_signature_role: "",
  transferee_signature_name: "",
  transferee_signature_role: D4PS_DEFAULTS.transferee_signature_role,

  hazardous: false,
  notes: "",
});

function Section({ title, hint, children }) {
  return (
    <Card className="rounded-sm border border-slate-200 shadow-none p-6">
      <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">{title}</div>
      {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">{children}</div>
    </Card>
  );
}

function Field({ label, value, onChange, testid, type = "text", placeholder, required, fullWidth }) {
  return (
    <div className={fullWidth ? "md:col-span-2" : ""}>
      <Label className="text-xs text-slate-600">
        {label}{required && <span className="text-red-500"> *</span>}
      </Label>
      <Input
        data-testid={testid}
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 rounded-sm border-slate-300"
      />
    </div>
  );
}

function Area({ label, value, onChange, testid, placeholder, rows = 3, fullWidth = true }) {
  return (
    <div className={fullWidth ? "md:col-span-2" : ""}>
      <Label className="text-xs text-slate-600">{label}</Label>
      <textarea
        data-testid={testid}
        rows={rows}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-sm border border-slate-300 mt-1 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
      />
    </div>
  );
}

function PickField({ label, value, onChange, options, testid, required }) {
  return (
    <div>
      <Label className="text-xs text-slate-600">{label}{required && <span className="text-red-500"> *</span>}</Label>
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger data-testid={testid} className="mt-1 rounded-sm border-slate-300">
          <SelectValue placeholder="Choose…" />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

export default function WasteNoteForm() {
  const { id } = useParams();
  const isNew = id === "new" || !id;
  const navigate = useNavigate();
  const [form, setForm] = useState(empty());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedNumber, setSavedNumber] = useState("");

  useEffect(() => {
    if (isNew) return;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/waste-notes/${id}`);
        setForm({ ...empty(), ...r.data });
        setSavedNumber(r.data.number || "");
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, [id, isNew]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.waste_description) return toast.error("Description of waste is required");
    if (!form.transferor_full_name) return toast.error("Transferor name is required");
    if (!form.transferee_full_name) return toast.error("Transferee name is required");
    setSaving(true);
    try {
      if (isNew) {
        const r = await api.post("/waste-notes", form);
        toast.success(`Waste note ${r.data.number} created`);
        navigate(`/waste-notes/${r.data.id}`);
      } else {
        await api.put(`/waste-notes/${id}`, form);
        toast.success("Waste note updated");
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="text-sm text-slate-500">Loading…</div>;

  return (
    <div data-testid="wtn-form" className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <Link to="/waste-notes" className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 mb-2">
            <ChevronLeft className="w-3 h-3" /> Back to waste notes
          </Link>
          <h1 className="font-display text-3xl tracking-tight font-semibold text-slate-900">
            {isNew ? "New waste transfer note" : `Edit ${savedNumber}`}
          </h1>
          <p className="text-xs text-slate-500 mt-1">Duty of Care – Section 34 Environmental Protection Act 1990.</p>
        </div>
        <div className="flex items-center gap-2">
          {!isNew && (
            <a
              data-testid="wtn-form-pdf"
              href={fileUrl(`/waste-notes/${id}/pdf`)}
              download rel="noopener"
              className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-sm border border-slate-300 text-slate-700 hover:bg-slate-50"
            ><Download className="w-4 h-4" /> PDF</a>
          )}
          <Button onClick={submit} disabled={saving} data-testid="wtn-save" className="rounded-sm bg-slate-900 hover:bg-slate-800">
            <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : (isNew ? "Create" : "Save changes")}
          </Button>
        </div>
      </div>

      {/* Section A */}
      <Section title="Section A – Description of Waste">
        <Area label="Description of the waste being transferred" value={form.waste_description} onChange={(v) => set("waste_description", v)} testid="wtn-waste-desc" rows={2} placeholder="e.g. Waste wooden pallets for recycling" />
        <Field label="List of Waste Regulations code(s)" value={form.ewc_codes} onChange={(v) => set("ewc_codes", v)} testid="wtn-ewc" placeholder="e.g. 15 01 03 – Wooden packaging" fullWidth />
        <PickField label="How is the waste contained?" value={form.container_type} onChange={(v) => set("container_type", v)} options={CONTAINER_OPTIONS} testid="wtn-container" />
        <Field label="How much waste? (e.g. number of sacks, weight)" value={form.quantity_description} onChange={(v) => set("quantity_description", v)} testid="wtn-quantity" placeholder="e.g. 7 wooden pallets, 250 kg" />
        <div className="md:col-span-2 flex items-center justify-between bg-slate-50 rounded-sm p-3 border border-slate-200">
          <div className="flex items-center gap-2">
            <AlertTriangle className={`w-4 h-4 ${form.hazardous ? "text-red-600" : "text-slate-400"}`} />
            <div>
              <div className="text-sm font-medium text-slate-900">Hazardous waste</div>
              <div className="text-xs text-slate-500">Toggle on if the load contains controlled hazardous waste.</div>
            </div>
          </div>
          <Switch data-testid="wtn-hazardous" checked={form.hazardous} onCheckedChange={(v) => set("hazardous", v)} />
        </div>
      </Section>

      {/* Section B */}
      <Section title="Section B – Current holder of the waste – Transferor">
        <Field label="Full name" value={form.transferor_full_name} onChange={(v) => set("transferor_full_name", v)} testid="wtn-transferor-name" required />
        <Field label="SIC code (2007)" value={form.transferor_sic_code} onChange={(v) => set("transferor_sic_code", v)} testid="wtn-transferor-sic" />
        <Area label="Company name and address" value={form.transferor_company_address} onChange={(v) => set("transferor_company_address", v)} testid="wtn-transferor-address" rows={4} placeholder="One line per address line" />
        <Field label="Postcode" value={form.transferor_postcode} onChange={(v) => set("transferor_postcode", v)} testid="wtn-transferor-postcode" />
        <Field label="Name of your unitary authority or council" value={form.transferor_local_authority} onChange={(v) => set("transferor_local_authority", v)} testid="wtn-transferor-authority" placeholder="e.g. Tameside Metropolitan Borough Council" />
        <PickField label="Are you?" value={form.transferor_type} onChange={(v) => set("transferor_type", v)} options={TRANSFEROR_TYPES} testid="wtn-transferor-type" />
      </Section>

      {/* Section C */}
      <Section title="Section C – Person Collecting the Waste – Transferee" hint="Pre-filled with D4PS Couriers & Logistics details — edit if a different carrier is collecting.">
        <Field label="Full name" value={form.transferee_full_name} onChange={(v) => set("transferee_full_name", v)} testid="wtn-transferee-name" required />
        <Field label="Postcode" value={form.transferee_postcode} onChange={(v) => set("transferee_postcode", v)} testid="wtn-transferee-postcode" />
        <Area label="Company name and address" value={form.transferee_company_address} onChange={(v) => set("transferee_company_address", v)} testid="wtn-transferee-address" rows={4} />
        <Field label="Registered waste carrier number" value={form.transferee_carrier_no} onChange={(v) => set("transferee_carrier_no", v)} testid="wtn-transferee-carrier" />
        <Field label="Details" value={form.transferee_details} onChange={(v) => set("transferee_details", v)} testid="wtn-transferee-details" placeholder="e.g. Upper Tier Waste Carrier, Broker and Dealer" fullWidth />
        <Area label="Address of transfer or collection point" value={form.transfer_address} onChange={(v) => set("transfer_address", v)} testid="wtn-transfer-address" rows={4} placeholder="Where the waste was collected from" />
        <Field label="Postcode" value={form.transfer_postcode} onChange={(v) => set("transfer_postcode", v)} testid="wtn-transfer-postcode" />
        <Field label="Date of transfer" value={form.date} onChange={(v) => set("date", v)} type="date" testid="wtn-date" required />
        <Field label="Time(s)" value={form.transfer_time} onChange={(v) => set("transfer_time", v)} type="time" testid="wtn-time" />
        <Field label="Facility / disposal location" value={form.facility} onChange={(v) => set("facility", v)} testid="wtn-facility" placeholder="e.g. Cheshire Recycling Centre, Sold, etc." fullWidth />
      </Section>

      {/* Broker */}
      <Section title="Broker or dealer who arranged this transfer (if applicable)">
        <Field label="Registration number" value={form.broker_registration_no} onChange={(v) => set("broker_registration_no", v)} testid="wtn-broker-reg" fullWidth />
      </Section>

      {/* Section D */}
      <Section title="Section D – The Transfer">
        <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <div className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Transferor</div>
            <Field label="Name" value={form.transferor_signature_name} onChange={(v) => set("transferor_signature_name", v)} testid="wtn-transferor-sig-name" placeholder="Person signing on behalf of producer" />
            <Field label="Representing" value={form.transferor_signature_role} onChange={(v) => set("transferor_signature_role", v)} testid="wtn-transferor-sig-role" placeholder="e.g. Public Health" />
            <div className="text-[11px] text-slate-500">Signature is captured on the printed PDF.</div>
          </div>
          <div className="space-y-3">
            <div className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Transferee</div>
            <Field label="Name" value={form.transferee_signature_name} onChange={(v) => set("transferee_signature_name", v)} testid="wtn-transferee-sig-name" placeholder="Driver / collector name" />
            <Field label="Representing" value={form.transferee_signature_role} onChange={(v) => set("transferee_signature_role", v)} testid="wtn-transferee-sig-role" />
            <div className="text-[11px] text-slate-500">Signature is captured on the printed PDF.</div>
          </div>
        </div>
      </Section>

      <Section title="Internal notes (not on PDF)">
        <Area label="" value={form.notes} onChange={(v) => set("notes", v)} testid="wtn-notes" rows={3} />
      </Section>
    </div>
  );
}
